/**
 * Omise.js loader and the calls we make through it.
 *
 * Why this runs in the browser at all: our servers never see payment details.
 * The payment gateway hands us a per-payment publishable key, the browser
 * exchanges it with Omise for a source (PromptPay) or token (card) id, and
 * only that id comes back to us — a card number never reaches our servers,
 * only Omise's.
 *
 * The script is loaded on demand rather than in index.html: every visitor to
 * every page would otherwise pay for a third-party script that only the Top-up
 * screen uses, and only after the customer has committed to paying.
 */

const OMISE_SCRIPT_URL = 'https://cdn.omise.co/omise.js';

interface OmiseGlobal {
  setPublicKey(key: string): void;
  createSource(
    type: string,
    options: { amount: number; currency: string },
    callback: (statusCode: number, response: { id?: string; message?: string; code?: string }) => void
  ): void;
  createToken(
    type: string,
    card: {
      name: string;
      number: string;
      expiration_month: number;
      expiration_year: number;
      security_code: string;
    },
    callback: (statusCode: number, response: { id?: string; message?: string; code?: string }) => void
  ): void;
}

declare global {
  interface Window {
    Omise?: OmiseGlobal;
  }
}

/** Distinguishes "the script never loaded" from "Omise rejected the source". */
export class OmiseUnavailableError extends Error {}
export class OmiseSourceError extends Error {}

let loader: Promise<OmiseGlobal> | null = null;

/**
 * Loads Omise.js once and resolves with the global it installs.
 *
 * The promise is cached including its rejection, then cleared, so a customer
 * who retries after a network blip gets a fresh attempt rather than the
 * remembered failure.
 */
function loadOmise(): Promise<OmiseGlobal> {
  if (window.Omise) return Promise.resolve(window.Omise);
  if (loader) return loader;

  loader = new Promise<OmiseGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = OMISE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Omise) resolve(window.Omise);
      // Loaded but installed nothing: treat as unavailable rather than
      // throwing a TypeError somewhere further down.
      else reject(new OmiseUnavailableError('Omise.js loaded but did not initialise'));
    };
    script.onerror = () =>
      reject(new OmiseUnavailableError(`could not load ${OMISE_SCRIPT_URL}`));
    document.head.appendChild(script);
  });

  loader = loader.catch((e: unknown) => {
    loader = null;
    throw e;
  });
  return loader;
}

/**
 * Creates a PromptPay source and returns its id.
 *
 * `amountSatang` comes from the server rather than being derived here on
 * purpose: `parseFloat('500.00') * 100` is a float operation, and money is the
 * one place this codebase does not do those.
 */
export async function createPromptPaySource(publicKey: string, amountSatang: number): Promise<string> {
  const omise = await loadOmise();
  omise.setPublicKey(publicKey);

  return new Promise<string>((resolve, reject) => {
    omise.createSource('promptpay', { amount: amountSatang, currency: 'THB' }, (statusCode, response) => {
      if (statusCode === 200 && response.id) {
        resolve(response.id);
        return;
      }
      reject(new OmiseSourceError(response.message ?? `Omise refused the payment source (${statusCode})`));
    });
  });
}

/** What the card form collects. Never sent anywhere but Omise. */
export interface CardDetails {
  name: string;
  number: string;
  expirationMonth: number;
  expirationYear: number;
  securityCode: string;
}

/**
 * Tokenizes card details with Omise and returns the token id.
 *
 * Reuses `OmiseSourceError` for a rejected/invalid card rather than a
 * dedicated error type: the caller (TopUp.tsx) already branches on that type
 * to show "the payment provider could not prepare this payment", which is
 * exactly as true for a declined tokenization as for a rejected source.
 */
export async function createCardToken(publicKey: string, card: CardDetails): Promise<string> {
  const omise = await loadOmise();
  omise.setPublicKey(publicKey);

  return new Promise<string>((resolve, reject) => {
    omise.createToken(
      'card',
      {
        name: card.name,
        number: card.number,
        expiration_month: card.expirationMonth,
        expiration_year: card.expirationYear,
        security_code: card.securityCode,
      },
      (statusCode, response) => {
        if (statusCode === 200 && response.id) {
          resolve(response.id);
          return;
        }
        reject(new OmiseSourceError(response.message ?? `Omise refused the card (${statusCode})`));
      }
    );
  });
}
